import React, { useState } from 'react';
import { StarIcon, CheckCircleIcon, PenIcon, SendIcon, XIcon } from 'lucide-react';
// Helper component for star ratings
function StarRating({
  rating
}: {
  rating: number;
}) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;
  const emptyStars = 5 - Math.ceil(rating);
  return <div className="flex gap-0.5 mb-3" aria-label={`${rating} out of 5 stars`} data-id="element-540">
      {[...Array(fullStars)].map((_, i) => <StarIcon key={`full-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-541" />)}

      {hasHalfStar && <div className="relative w-4 h-4" data-id="element-542">
          <StarIcon className="absolute top-0 left-0 w-4 h-4 text-gray-300" data-id="element-543" />
          <div className="absolute top-0 left-0 w-2 overflow-hidden" data-id="element-544">
            <StarIcon className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-545" />
          </div>
        </div>}

      {[...Array(emptyStars)].map((_, i) => <StarIcon key={`empty-${i}`} className="w-4 h-4 text-gray-300" data-id="element-546" />)}
    </div>;
}
interface TestimonialData {
  id: number;
  name: string;
  initials: string;
  location: string;
  rating: number;
  quote: string;
  color1: string;
  color2: string;
}
const testimonials: TestimonialData[] = [{
  id: 1,
  name: 'Sarah K.',
  initials: 'SK',
  location: 'Minneapolis, MN',
  rating: 5,
  quote: 'I live in Zone 5b in Minneapolis, Minnesota. Last February we had three consecutive nights below -15°F and I was sure I was going to lose everything. I followed the frost protocol in this guide the night before the cold snap hit and every single one of my 14 perennials made it through. My neighbor lost 6 of her rose bushes that same week. This guide is the real deal.',
  color1: '#B7E4C7',
  color2: '#74C69D'
}, {
  id: 2,
  name: 'Marcus T.',
  initials: 'MT',
  location: 'Phoenix, AZ',
  rating: 5,
  quote: "I'm in Phoenix, Arizona, Zone 10a. Summers here are brutal — we hit 115°F last July and I watched $400 worth of plants fry in 48 hours. A friend sent me this guide and I used the heat shield method on my remaining beds. Not one plant died for the rest of the summer. I wish I had found this before July.",
  color1: '#D8F3DC',
  color2: '#52B788'
}, {
  id: 3,
  name: 'Linda R.',
  initials: 'LR',
  location: 'Portland, OR',
  rating: 5,
  quote: 'Honestly, I bought this mostly for the weather section and almost ignored the pest bonus. Big mistake. I had a slug problem so bad that I was losing entire seedlings overnight. The eggshell and cinnamon method in the bonus guide wiped out the problem in 4 days. No chemicals, no expensive products from Home Depot. Just stuff I already had in my kitchen. I was shocked.',
  color1: '#95D5B2',
  color2: '#40916C'
}, {
  id: 4,
  name: 'David M.',
  initials: 'DM',
  location: 'Nashville, TN',
  rating: 5,
  quote: "I'll be honest — I almost didn't buy this because I've wasted money on gardening guides before that were just generic tips I could find on Google. This one is completely different. It's broken down by season and by USDA zone, so the advice actually applies to where I live in Nashville, Tennessee. I followed the fall prep checklist in October and my garden came back stronger this spring than it ever has in 6 years of trying.",
  color1: '#74C69D',
  color2: '#2D6A4F'
}, {
  id: 5,
  name: 'Jennifer L.',
  initials: 'JL',
  location: 'Charlotte, NC',
  rating: 5,
  quote: "I'm not a professional gardener — I just love my front yard and I've spent a lot of time and energy making it look beautiful. Last spring I lost 3 hydrangeas and 2 knockout rose bushes to a late frost in late April. It was heartbreaking. I found this guide in May and followed the full season plan from summer through fall. This past April, when we got hit with another late frost, every single plant made it. My hydrangeas are blooming right now and they have never looked better. Worth every penny.",
  color1: '#52B788',
  color2: '#1B4332'
}];
function TestimonialCard({
  data
}: {
  data: TestimonialData;
}) {
  return <div className="bg-cream rounded-xl p-5 shadow-sm border border-cream-dark flex flex-col" data-id="element-547">
      <div className="flex items-center gap-3 mb-4" data-id="element-548">
        <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center shadow-inner" style={{
        background: `linear-gradient(135deg, ${data.color1} 0%, ${data.color2} 100%)`
      }} data-id="element-549">
          <span className="text-forest-dark font-bold text-sm font-sans" data-id="element-550">
            {data.initials}
          </span>
        </div>
        <div data-id="element-551">
          <p className="font-sans font-semibold text-forest-dark text-sm leading-tight" data-id="element-552">
            {data.name}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5" data-id="element-553">
            <p className="font-sans text-gray-500 text-xs" data-id="element-554">{data.location}</p>
            <span className="text-gray-300 text-xs" data-id="element-555">•</span>
            <div className="flex items-center gap-0.5" data-id="element-556">
              <CheckCircleIcon className="w-3 h-3 text-forest" data-id="element-557" />
              <span className="text-forest text-[10px] font-semibold uppercase tracking-wider" data-id="element-558">
                Verified
              </span>
            </div>
          </div>
        </div>
      </div>

      <StarRating rating={data.rating} data-id="element-559" />

      <blockquote className="font-sans text-gray-700 leading-relaxed text-[15px] italic flex-1" data-id="element-560">
        "{data.quote}"
      </blockquote>
    </div>;
}
export function TestimonialCarousel() {
  const [showForm, setShowForm] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewLocation, setReviewLocation] = useState('');
  const [selectedStars, setSelectedStars] = useState(5);
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = () => {
    if (reviewText.trim() && reviewName.trim()) {
      setSubmitted(true);
      setTimeout(() => {
        setShowForm(false);
        setSubmitted(false);
        setReviewText('');
        setReviewName('');
        setReviewLocation('');
        setSelectedStars(5);
      }, 3000);
    }
  };
  return <section className="bg-white py-12 md:py-16" data-id="element-561">
      <div className="max-w-5xl mx-auto px-5 mb-10" data-id="element-562">
        <h2 className="font-serif text-forest text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-563">
          What Real Gardeners Are Saying
        </h2>
        <p className="text-gray-500 text-center font-sans" data-id="element-564">
          Join 4,700+ homeowners protecting their gardens
        </p>
      </div>

      {/* Static Testimonials */}
      <div className="max-w-3xl mx-auto px-5 flex flex-col gap-6" data-id="element-565">
        {testimonials.map(item => <TestimonialCard key={item.id} data={item} data-id="element-566" />)}
      </div>

      {/* Write a Review Section */}
      <div className="max-w-lg mx-auto px-5 mt-12" data-id="element-567">
        {!showForm ? <div className="bg-cream-dark rounded-xl p-6 text-center border border-gray-200/50" data-id="element-568">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-569">
              <PenIcon className="w-5 h-5 text-forest" data-id="element-570" />
            </div>
            <h3 className="font-semibold text-forest font-sans mb-2" data-id="element-571">
              Share Your Garden Story
            </h3>
            <p className="text-gray-500 text-sm font-sans mb-5" data-id="element-572">
              Have you used The 365 Garden Shield? We'd love to hear about your
              results.
            </p>
            <button onClick={() => setShowForm(true)} className="bg-forest text-white rounded-lg py-2.5 px-6 text-sm font-semibold hover:bg-forest-dark transition-colors shadow-sm" data-id="element-573">
              Write a Review
            </button>
          </div> : <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg" data-id="element-574">
            {submitted ? <div className="text-center py-8" data-id="element-575">
                <div className="w-14 h-14 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-576">
                  <CheckCircleIcon className="w-7 h-7 text-forest" data-id="element-577" />
                </div>
                <h3 className="font-semibold text-forest font-sans text-lg mb-2" data-id="element-578">
                  Thank You! 🌿
                </h3>
                <p className="text-gray-500 text-sm font-sans" data-id="element-579">
                  Your review has been submitted and will appear shortly.
                </p>
              </div> : <>
                {/* Header */}
                <div className="flex items-center justify-between mb-5" data-id="element-580">
                  <h3 className="font-semibold text-forest font-sans text-lg" data-id="element-581">
                    Write Your Review
                  </h3>
                  <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors" data-id="element-582">
                    <XIcon className="w-4 h-4 text-gray-500" data-id="element-583" />
                  </button>
                </div>

                {/* Star Selector */}
                <div className="mb-4" data-id="element-584">
                  <p className="text-sm text-gray-500 font-sans mb-2" data-id="element-585">
                    Your Rating
                  </p>
                  <div className="flex gap-1" data-id="element-586">
                    {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setSelectedStars(star)} className="focus:outline-none transition-transform hover:scale-110" data-id="element-587">
                        <StarIcon className={`w-7 h-7 ${star <= selectedStars ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} data-id="element-588" />
                      </button>)}
                  </div>
                </div>

                {/* Name & Location */}
                <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-589">
                  <div data-id="element-590">
                    <label className="text-sm text-gray-500 font-sans mb-1 block" data-id="element-591">
                      Your Name *
                    </label>
                    <input type="text" value={reviewName} onChange={e => setReviewName(e.target.value)} placeholder="e.g. Sarah M." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest placeholder:text-gray-400" data-id="element-592" />
                  </div>
                  <div data-id="element-593">
                    <label className="text-sm text-gray-500 font-sans mb-1 block" data-id="element-594">
                      Location
                    </label>
                    <input type="text" value={reviewLocation} onChange={e => setReviewLocation(e.target.value)} placeholder="e.g. Texas" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest placeholder:text-gray-400" data-id="element-595" />
                  </div>
                </div>

                {/* Review Text */}
                <div className="mb-5" data-id="element-596">
                  <label className="text-sm text-gray-500 font-sans mb-1 block" data-id="element-597">
                    Your Review *
                  </label>
                  <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Tell us how The 365 Garden Shield helped your garden..." rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-sans text-gray-700 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest placeholder:text-gray-400 resize-none" data-id="element-598" />
                </div>

                {/* Submit */}
                <button onClick={handleSubmit} disabled={!reviewText.trim() || !reviewName.trim()} className="w-full bg-forest text-white rounded-lg py-3 px-6 text-sm font-semibold hover:bg-forest-dark transition-colors shadow-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-599">
                  <SendIcon className="w-4 h-4" data-id="element-600" />
                  Submit Review
                </button>
              </>}
          </div>}
      </div>
    </section>;
}